import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  CheckCircle,
  AlertCircle,
  Loader,
  Building2,
  ArrowRight,
  MessageCircle
} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    subject: '',
    message: '' 
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'

  const easePremium = [0.22, 1, 0.36, 1];

  // Número de WhatsApp de la empresa (sin el + ni espacios)
  const WHATSAPP_NUMBER = "51955000321"; 

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Simular un pequeño delay para mejor UX (sensación de procesamiento)
      await new Promise(resolve => setTimeout(resolve, 800));

      // 1. Construir el mensaje de WhatsApp con formato profesional
      const whatsappMessage = `Hola Visioner7, me gustaría solicitar información sobre sus servicios.%0A%0A` +
        `*Nombre:* ${formData.name}%0A` +
        `*Correo:* ${formData.email}%0A` +
        `*Teléfono:* ${formData.phone || 'No proporcionado'}%0A` +
        `*Área de Interés:* ${formData.subject}%0A` +
        `*Detalles del Proyecto:*%0A${formData.message}`;

      // 2. Codificar el mensaje para URL
      const encodedMessage = encodeURIComponent(whatsappMessage)
        .replace(/%250A/g, '%0A'); // Mantener los saltos de línea limpios

      // 3. Crear la URL de WhatsApp
      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

      // 4. Abrir WhatsApp en una nueva pestaña
      window.open(whatsappUrl, '_blank');

      // 5. Mostrar estado de éxito y limpiar formulario
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { 
      icon: Mail, 
      label: 'Correo Electrónico', 
      value: 'contacto@visioner7.com',
      link: 'mailto:contacto@visioner7.com'
    },
    { 
      icon: Phone, 
      label: 'Teléfono Corporativo', 
      value: '+51 955 000 321',
      link: `https://wa.me/${WHATSAPP_NUMBER}`
    },
    { 
      icon: MapPin, 
      label: 'Oficina Principal', 
      value: 'Calle Octavio Muñoz Najar 227, Lima',
      link: 'https://maps.google.com'
    }
  ];

  return (
    <section id="contacto" className="py-24 lg:py-32 bg-slate-50 relative overflow-hidden">
      
      {/* Fondo con degradado sutil para dar profundidad */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-white pointer-events-none" />
      
      {/* Orbe de luz ambiental sutil (verde corporativo) */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 lg:px-12 relative z-10">
        
        {/* Encabezado */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: easePremium }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-4 uppercase tracking-widest">
            <Building2 className="w-4 h-4" />
            Contacto Empresarial
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight leading-[1.15] mb-6">
            Iniciemos una <span className="text-emerald-600">conversación</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Complete el formulario. Será redirigido a WhatsApp con un mensaje preformateado 
            para que nuestro equipo le atienda de inmediato.
          </p>
        </motion.div>

        {/* ========================================== */}
        {/* TARJETA MAESTRA DIVIDIDA (SPLIT LAYOUT)    */}
        {/* ========================================== */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: easePremium }}
          className="max-w-6xl mx-auto bg-white rounded-3xl shadow-2xl shadow-slate-200/50 overflow-hidden border border-slate-100"
        >
          <div className="grid lg:grid-cols-5">
            
            {/* COLUMNA IZQUIERDA: Panel Oscuro Premium */}
            <div className="lg:col-span-2 bg-slate-900 text-white p-8 md:p-12 relative overflow-hidden flex flex-col justify-between">
              {/* Efecto de luz sutil en la esquina */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
              
              <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-2 tracking-tight">Información de Contacto</h3>
                <p className="text-slate-400 mb-10 text-sm leading-relaxed">
                  Estamos listos para escuchar sus necesidades y ofrecer la mejor solución tecnológica.
                </p>

                <div className="space-y-8">
                  {contactInfo.map((info, index) => {
                    const Icon = info.icon;
                    return (
                      <motion.a
                        key={info.label}
                        href={info.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1, duration: 0.5 }}
                        className="group flex items-start gap-4"
                      >
                        <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/20 group-hover:border-emerald-500/30 transition-all duration-300">
                          <Icon className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
                            {info.label}
                          </p>
                          <p className="text-base font-medium text-white group-hover:text-emerald-400 transition-colors">
                            {info.value}
                          </p>
                        </div>
                      </motion.a>
                    );
                  })}
                </div>
              </div>

              {/* Footer del panel oscuro */}
              <div className="relative z-10 mt-12 pt-8 border-t border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <MessageCircle className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm font-semibold text-white">Respuesta rápida por WhatsApp</span>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  <strong className="text-slate-300">Compromiso de Privacidad:</strong><br />
                  Sus datos se envían de forma segura y directa a nuestro equipo comercial.
                </p>
              </div>
            </div>

            {/* COLUMNA DERECHA: Formulario Limpio y Elevado */}
            <div className="lg:col-span-3 p-8 md:p-12 lg:p-16 bg-white">
              <AnimatePresence mode="wait">
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-8 p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-emerald-900">¡Redirigiendo a WhatsApp!</p>
                      <p className="text-sm text-emerald-700 mt-1">Se ha abierto una nueva pestaña con su mensaje preformateado. Solo debe presionar "Enviar".</p>
                    </div>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-8 p-4 bg-rose-50 border border-rose-100 rounded-xl flex items-start gap-3"
                  >
                    <AlertCircle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-rose-900">Error al procesar</p>
                      <p className="text-sm text-rose-700 mt-1">Por favor, verifique su conexión e intente nuevamente.</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Nombre Completo <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all duration-300 outline-none"
                      placeholder="Ej. Carlos Mendoza"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Correo Corporativo <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all duration-300 outline-none"
                      placeholder="carlos@empresa.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="phone" className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Teléfono
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all duration-300 outline-none"
                      placeholder="+51 999 999 999"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="subject" className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Área de Interés <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all duration-300 outline-none appearance-none cursor-pointer"
                        required
                      >
                        <option value="" disabled>Seleccione una opción</option>
                        <option value="Desarrollo de Software">Desarrollo de Software a Medida</option>
                        <option value="Sistemas Contables">Sistemas de Facturación / Contabilidad</option>
                        <option value="Consultoría TI">Consultoría Tecnológica</option>
                        <option value="Soporte Técnico">Soporte Técnico</option>
                        <option value="Otro">Otro</option>
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="6 9 12 15 18 9"></polyline></svg>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Detalles del Proyecto <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all duration-300 outline-none resize-none"
                    placeholder="Describa brevemente sus necesidades, plazos o requerimientos específicos..."
                    required
                  ></textarea>
                </div>

                <div className="pt-4">
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: isSubmitting ? 1 : 1.01 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.99 }}
                    className="group w-full md:w-auto bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-3 transition-all duration-300 hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-600/25 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        <span>Preparando mensaje...</span>
                      </>
                    ) : (
                      <>
                        <MessageCircle className="w-5 h-5" />
                        <span>Enviar por WhatsApp</span>
                        <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </motion.button>
                  <p className="mt-4 text-xs text-slate-400 text-center md:text-left">
                    Al hacer clic, se abrirá WhatsApp Web o la aplicación con un mensaje preformateado listo para enviar.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;